import React, { useCallback, useEffect, useState } from 'react';
import type { EmblaOptionsType } from 'embla-carousel-react';
import useEmblaCarousel from 'embla-carousel-react';

import { Thumb } from '../buttons';

type PropType = {
  slides: number[];
  options?: EmblaOptionsType;
  images: string[];
  selectedIndexOut: number;
  onNewIndex: (index: number) => void;
  elements: () => JSX.Element[];
  showThumbs?: boolean;
};

const ImageCarousel: React.FC<PropType> = ({
  onNewIndex,
  selectedIndexOut,
  slides,
  options,
  images,
  elements,
  showThumbs,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(selectedIndexOut);

  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({});

  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel(options);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;

      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;

    setSelectedIndex(emblaMainApi.selectedScrollSnap());

    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;

    setSelectedIndex(selectedIndexOut);

    emblaMainApi.scrollTo(selectedIndexOut);
  }, [selectedIndexOut, emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    onNewIndex(selectedIndex);
  }, [selectedIndex, onNewIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;

    onSelect();

    emblaMainApi.on('select', onSelect);

    emblaMainApi.on('reInit', onSelect);
  }, [emblaMainApi, onSelect]);

  const thumbs = () => {
    return slides.map((index, i) => (
      <Thumb
        onClick={() => onThumbClick(index)}
        selected={index === selectedIndex}
        index={index}
        imgSrc={images[index % images.length]}
        key={index}
      />
    ));
  };

  return (
    <>
      <div className="image-slider">
        <div className="embla-carousel-thumb">
          <div className="carousel-thumb">
            <div className="carousel-thumb-viewport is-draggable" ref={emblaThumbsRef}>
              <div className="carousel-thumb-container">{showThumbs && thumbs()}</div>
            </div>
          </div>
        </div>
        <div className="embla-carousel-main">
          <div className="carousel-main">
            <div className="carousel-main-viewport" ref={emblaMainRef}>
              <div className="carousel-main-container">{elements()}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImageCarousel;
